import { useAtom } from 'ximple';
import { capitalize } from '@mui/material';
import {
    BilberryWidgetsGlobal,
    CheckoutInfo,
    GiftcardPaymentInfo,
    PaymentInfo,
    Translations,
} from '@repo/types';
import { checkoutInfoAtom } from 'src/state/checkout-info/checkoutInfoAtom';
import { cartAtom } from 'src/state/cart/cartAtom';
import { paymentPlanAtom } from 'src/state/payment-plan/paymentPlanAtom';
import {
    CheckoutTabSet,
    CheckoutTabSetTab,
    CheckoutTabType,
    getTabIndex,
    IdxOpts,
} from './views/getTabIndex';
import PackageView from './views/PackageView';
import { PaymentView } from './views/PaymentView';
import IntentView from 'src/components/domain/timeslots-views/IntentView';
import { ContactView } from './views/ContactView';
import { MemberCardPaymentView } from './views/MemberCardPaymentView';
import { ExtrasView } from './views/ExtrasView';
import { OctoExtrasView } from './views/OctoExtrasView';
import { RelatedProductsView } from './views/RelatedProductsView';
import { usePayment } from './usePayment';
import { useCartCheckoutContext } from './CartCheckoutContext';
import { useLocale } from '@repo/i18n';
import CheckoutTabs from 'src/components/domain/checkout/CheckoutTabs';
import EmptyCartView from './views/EmptyCartView';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import useActiveOctoBookings from 'src/hooks/domain/useActiveOctoBookings';
import { useIsPaymentRequired } from 'src/hooks/domain/cart/useIsPaymentRequired';

import type { JSX } from 'react';

export function CartCheckoutTabs(): JSX.Element {
    const { t } = useLocale();
    const configuration = useConfigurations();
    const [cartItems] = useAtom(cartAtom);
    const [{ isUsingPaymentPlan }] = useAtom(paymentPlanAtom);
    const [checkoutInfoData] = useAtom(checkoutInfoAtom);
    const { octoOrderInCart, isLoadingOctoOrderInCart } = useCartCheckoutContext();

    const {
        setActiveTab,
        idxOpts,
        activeTab,
        onConfirmationPage,
        isCheckoutInfoDataValid,
        completedPaymentInfo,
    } = useCartCheckoutContext();
    const {
        handleGoPayClicked,
        onPaymentCancelled,
        onPaymentCompleted,
        clickedNext,
        setClickedNext,
    } = usePayment();

    const { isPaymentRequired } = useIsPaymentRequired();

    const activeOctoBookings = useActiveOctoBookings(octoOrderInCart);

    const hasNoCartItems =
        (cartItems.length === 0 && !isLoadingOctoOrderInCart && !octoOrderInCart) ||
        (octoOrderInCart && activeOctoBookings.length === 0);

    if (hasNoCartItems && !completedPaymentInfo) {
        return <EmptyCartView />;
    }

    const contactInfoTab = createContactInfoTab(
        idxOpts,
        t,
        setActiveTab,
        onConfirmationPage,
        checkoutInfoData,
        clickedNext,
        setClickedNext,
        isUsingPaymentPlan,
        isCheckoutInfoDataValid,
        handleGoPayClicked,
        configuration,
    );

    const paymentTab = createPaymentTab(
        idxOpts,
        t,
        onConfirmationPage,
        onPaymentCancelled,
        isCheckoutInfoDataValid,
        onPaymentCompleted,
        configuration,
    );

    const tabset: CheckoutTabSet = {
        ordered: false,
        tabs: [],
    };

    if (idxOpts.hasPackage) {
        tabset.tabs.push(
            createPackageTab(idxOpts, t, onConfirmationPage, setActiveTab, configuration),
        );
    }

    if (idxOpts.hasRelatedProducts) {
        tabset.tabs.push(
            createRelatedProductsTab(idxOpts, t, setActiveTab, onConfirmationPage, configuration),
        );
    }

    if (idxOpts.hasExtras) {
        tabset.tabs.push(
            createExtrasTab(idxOpts, t, setActiveTab, onConfirmationPage, configuration),
        );
    }

    if (idxOpts.hasOctoExtras) {
        tabset.tabs.push(
            createOctoExtrasTab(idxOpts, t, setActiveTab, onConfirmationPage, configuration),
        );
    }

    tabset.tabs.push(contactInfoTab);

    if (idxOpts.hasMemberCardStep) {
        tabset.tabs.push(
            createMemberCardTab(
                idxOpts,
                t,
                onConfirmationPage,
                handleGoPayClicked,
                checkoutInfoData,
                configuration,
            ),
        );
    }

    if (isUsingPaymentPlan) {
        tabset.tabs.push(
            createIntentTab(
                idxOpts,
                t,
                onConfirmationPage,
                handleGoPayClicked,
                configuration,
                isPaymentRequired,
            ),
        );
    }

    tabset.tabs.push(paymentTab);

    const activeTabNumber = getTabIndex(activeTab, { ...idxOpts, display: false });

    return (
        <CheckoutTabs
            tabset={tabset}
            currentTab={activeTabNumber}
            onChange={(e) => setActiveTab(tabset.tabs[e].identifier)}
        />
    );
}

function createRelatedProductsTab(
    idxOpts: IdxOpts,
    t: Translations,
    setActiveTab: (activeTab: CheckoutTabType) => void,
    onConfirmationPage: boolean,
    configuration: BilberryWidgetsGlobal,
): CheckoutTabSetTab {
    return {
        name: `${getTabIndex('relatedproducts', idxOpts)} ${t.related_products}`,
        title: capitalize(t.related_products),
        description: capitalize(t.please_select),
        backButtonUrl: configuration.continueShoppingUrl,
        backButtonText: t.continue_shopping,
        identifier: 'relatedproducts',
        content: (
            <RelatedProductsView
                onNextClicked={() =>
                    setActiveTab(
                        idxOpts.hasExtras
                            ? 'extras'
                            : idxOpts.hasOctoExtras
                              ? 'octoextras'
                              : 'contactinfo',
                    )
                }
            />
        ),
        disabled: onConfirmationPage,
    };
}

function createExtrasTab(
    idxOpts: IdxOpts,
    t: Translations,
    setActiveTab: (activeTab: CheckoutTabType) => void,
    onConfirmationPage: boolean,
    configuration: BilberryWidgetsGlobal,
): CheckoutTabSetTab {
    const disabled = onConfirmationPage || !idxOpts.hasExtras;

    return {
        name: `${getTabIndex('extras', idxOpts)} ${t.extras}`,
        title: capitalize(t.extras),
        description: capitalize(t.please_select),
        backButtonUrl: configuration.continueShoppingUrl,
        backButtonText: t.continue_shopping,
        identifier: 'extras',
        content: <ExtrasView onNextClicked={() => setActiveTab('contactinfo')} />,
        disabled: disabled,
        sx: {
            ...(!idxOpts.hasExtras && { textDecoration: 'line-through' }),
        },
    };
}

function createOctoExtrasTab(
    idxOpts: IdxOpts,
    t: Translations,
    setActiveTab: (activeTab: CheckoutTabType) => void,
    onConfirmationPage: boolean,
    configuration: BilberryWidgetsGlobal,
): CheckoutTabSetTab {
    return {
        name: `${getTabIndex('octoextras', idxOpts)} ${t.extras}`,
        title: capitalize(t.extras),
        description: capitalize(t.please_select),
        backButtonUrl: configuration.continueShoppingUrl,
        backButtonText: t.continue_shopping,
        identifier: 'octoextras',
        content: <OctoExtrasView onNextClicked={() => setActiveTab('contactinfo')} />,
        disabled: onConfirmationPage,
    };
}

function createContactInfoTab(
    idxOpts: IdxOpts,
    t: Translations,
    setActiveTab: (activeTab: CheckoutTabType) => void,
    onConfirmationPage: boolean,
    checkoutInfoData: CheckoutInfo | null,
    clickedNext: boolean,
    setClickedNext: React.Dispatch<React.SetStateAction<boolean>>,
    isUsingPaymentPlan: boolean,
    isCheckoutInfoDataValid: boolean,
    handleGoPayClicked: (paymentMethod?: 'invoice') => void,
    configuration: BilberryWidgetsGlobal,
): CheckoutTabSetTab {
    return {
        name: `${getTabIndex('contactinfo', idxOpts)} ${t.contact_details}`,
        identifier: 'contactinfo',
        title: capitalize(t.contact_details),
        description: capitalize(t.please_register),
        backButtonUrl: configuration.continueShoppingUrl,
        backButtonText: t.continue_shopping,
        content: (
            <ContactView
                clickedNext={clickedNext}
                handleGoPayClickedInvoice={
                    idxOpts.hasMemberCardStep ||
                    isUsingPaymentPlan ||
                    checkoutInfoData?.contactType !== 'company'
                        ? undefined
                        : () => handleGoPayClicked('invoice')
                }
                handleGoPayClicked={
                    // The native member-card step and the payment-plan step both interpose a
                    // "choose how to pay" step before reserving; otherwise the CTA reserves directly.
                    idxOpts.hasMemberCardStep
                        ? () =>
                              isCheckoutInfoDataValid
                                  ? Promise.resolve(setActiveTab('membercard'))
                                  : setClickedNext(true)
                        : isUsingPaymentPlan
                          ? () =>
                                isCheckoutInfoDataValid
                                    ? Promise.resolve(setActiveTab('intent'))
                                    : setClickedNext(true)
                          : () => handleGoPayClicked()
                }
                nextButtonText={
                    idxOpts.hasMemberCardStep || isUsingPaymentPlan
                        ? t.choose_payment_method
                        : t.go_and_pay
                }
            />
        ),
        disabled: onConfirmationPage,
    };
}

function createMemberCardTab(
    idxOpts: IdxOpts,
    t: Translations,
    onConfirmationPage: boolean,
    handleGoPayClicked: (paymentMethod?: 'invoice') => void,
    checkoutInfoData: CheckoutInfo | null,
    configuration: BilberryWidgetsGlobal,
): CheckoutTabSetTab {
    return {
        name: `${getTabIndex('membercard', idxOpts)} ${t.choose_payment_method}`,
        identifier: 'membercard',
        title: capitalize(t.choose_payment_method),
        description: capitalize(t.please_select),
        backButtonUrl: configuration.continueShoppingUrl,
        backButtonText: t.continue_shopping,
        content: (
            <MemberCardPaymentView
                handleGoPayClicked={() => handleGoPayClicked()}
                handleGoPayClickedInvoice={
                    configuration.enableInvoicePayment &&
                    checkoutInfoData?.contactType === 'company'
                        ? () => handleGoPayClicked('invoice')
                        : undefined
                }
            />
        ),
        disabled: onConfirmationPage,
    };
}

function createIntentTab(
    idxOpts: IdxOpts,
    t: Translations,
    onConfirmationPage: boolean,
    handleGoPayClicked: () => void,
    configuration: BilberryWidgetsGlobal,
    isPaymentRequired: boolean,
): CheckoutTabSetTab {
    const tabLabel = isPaymentRequired ? t.choose_payment_method : t.confirm_booking;
    return {
        name: `${getTabIndex('intent', idxOpts)} ${tabLabel}`,
        identifier: 'intent',
        title: capitalize(tabLabel),
        description: '',
        backButtonUrl: configuration.continueShoppingUrl,
        backButtonText: t.continue_shopping,
        content: (
            <IntentView
                handleGoPayClicked={handleGoPayClicked}
                isPaymentRequired={isPaymentRequired}
            />
        ),
        disabled: onConfirmationPage,
    };
}

function createPaymentTab(
    idxOpts: IdxOpts,
    t: Translations,
    onConfirmationPage: boolean,
    onPaymentCancelled: () => void,
    isCheckoutInfoDataValid: boolean,
    onPaymentCompleted: (
        IdxOpts: IdxOpts,
    ) => (paymentInfo: PaymentInfo | GiftcardPaymentInfo, paymentGatewayResponse: any) => void, // TODO: Refactor this stuff?
    configuration: BilberryWidgetsGlobal,
): CheckoutTabSetTab {
    return {
        name: `${getTabIndex('payment', idxOpts)} ${t.payment}`,
        identifier: 'payment',
        title: capitalize(t.payment),
        description: capitalize(t.please_select),
        backButtonUrl: configuration.continueShoppingUrl,
        backButtonText: t.continue_shopping,
        hideTitle: onConfirmationPage ? true : false,
        content: (
            <PaymentView
                onPaymentCancelled={onPaymentCancelled}
                onPaymentCompleted={onPaymentCompleted(idxOpts)}
            />
        ),
        disabled: !isCheckoutInfoDataValid || onConfirmationPage,
    };
}

function createPackageTab(
    idxOpts: IdxOpts,
    t: Translations,
    onConfirmationPage: boolean,
    setActiveTab: (activeTab: CheckoutTabType) => void,
    configuration: BilberryWidgetsGlobal,
): CheckoutTabSetTab {
    return {
        name: `${getTabIndex('package', idxOpts)} ${t.packages}`,
        identifier: 'package',
        title: capitalize(t.package),
        description: capitalize(t.pleaseChoose),
        backButtonUrl: configuration.continueShoppingUrl,
        backButtonText: t.continue_shopping,
        content: (
            <PackageView
                onNextClicked={() =>
                    setActiveTab(
                        idxOpts.hasRelatedProducts
                            ? 'relatedproducts'
                            : idxOpts.hasExtras
                              ? 'extras'
                              : idxOpts.hasOctoExtras
                                ? 'octoextras'
                                : 'contactinfo',
                    )
                }
                nextButtonText={
                    idxOpts.hasRelatedProducts
                        ? t.related_products
                        : idxOpts.hasExtras || idxOpts.hasOctoExtras
                          ? t.extras
                          : t.contact_details
                }
            />
        ),
        disabled: onConfirmationPage,
    };
}
